import clsx from "clsx";
import { useEffect, useRef } from "react";

type Props = {
  /** Mux playback ID OR a local / remote video path */
  id: string;
  className?: string;
  /** apply a CSS filter, e.g. "saturate(1.1) contrast(1.05)" */
  filter?: string;
  /** muted? defaults true (autoplay won't work otherwise) */
  muted?: boolean;
  /** loop? defaults true */
  loop?: boolean;
};

/**
 * Reliable video player. Works with:
 *  - Mux playback IDs → loads HLS stream
 *  - Local file paths (e.g. "/videos/hero.mp4") → plays directly
 *  - Remote URLs (e.g. "https://.../video.mp4") → plays directly
 *
 * For local/remote files, no HLS.js is needed — the browser plays
 * them natively. This eliminates network glitches from third-party
 * streaming services.
 */
export function MuxScreen({ id, className, filter, muted = true, loop = true }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isLocal = id.startsWith("/") || id.startsWith("http");

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // ── LOCAL / REMOTE FILE ──
    if (isLocal) {
      video.src = id;
      video.play().catch(() => {});
      return;
    }

    // ── MUX HLS STREAM ──
    const hlsUrl = `https://stream.mux.com/${id}.m3u8`;

    // Safari (and iOS WebKit) supports HLS natively
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
      video.play().catch(() => {});
      return;
    }

    // Everywhere else: lazy-load hls.js
    let hls: { destroy: () => void } | null = null;
    let cancelled = false;
    (async () => {
      const Hls = (await import("hls.js")).default;
      if (cancelled) return;
      if (Hls.isSupported()) {
        const inst = new Hls({ enableWorker: true, lowLatencyMode: false });
        inst.loadSource(hlsUrl);
        inst.attachMedia(video);
        inst.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
        hls = inst;
      } else {
        video.src = hlsUrl;
        video.play().catch(() => {});
      }
    })();

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [id, isLocal]);

  const poster = isLocal ? undefined : `https://image.mux.com/${id}/thumbnail.jpg?width=1920&fit_mode=preserve`;

  return (
    <div className={clsx("overflow-hidden bg-ink-1000", className)} style={{ position: "relative" }}>
      <video
        ref={videoRef}
        poster={poster}
        muted={muted}
        loop={loop}
        autoPlay
        playsInline
        preload="auto"
        crossOrigin={isLocal ? undefined : "anonymous"}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
          filter,
        }}
      />
    </div>
  );
}
